import React, { useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
  Collapse,
  InputGroupAddon,
} from 'reactstrap';
import * as monetizationOrdersActions from '../../store/monetizationOrders/actions';
import { selectMessageTemplateByMonetizationOrder } from '../../selectors/messageTemplate';
import { selectSupplierByType } from '../../selectors/supplier';
import { actualCountry } from '../../selectors/country';
import { useDispatch, useSelector } from 'react-redux';

import QueryBuilder from '../Common/QueryBuilder';
import { Form } from '@availity/form';
import { Input, Select, Date, Switch } from '../../lib/form-field';
import '@availity/yup';
import formValidation from './formValidation';
import { SubmitButton } from '../Common/Modal/modalActionButtons';
import { components } from 'react-select';
import { capitalize } from '../../helpers/sharedFunction';

import customerWebServices from '../../config/customerWebServices';
import sendLeadTypes from '../../config/sendLeadTypes';
import supplierTypes from '../../config/supplierTypes';
import { defaultSendSchedule } from '../../config/sendScheduleSlots';
import SendScheduleMatrix from './SendScheduleMatrix';
import { useTranslation } from 'react-i18next';
import { writeInCountryLanguage } from '../../helpers/countryLanguage';

const { Option } = components;

const MessageTemplateSelectOption = (props) => (
  <Option {...props}>
    {props.data.type
      ? `${capitalize(props.data.type)}${
          props.data.linkType ? ' - ' + capitalize(props.data.linkType) : ''
        } - ${props.data.label}`
      : props.data.label}
  </Option>
);

const messageTemplateForSelect = (messageTemplates) =>
  messageTemplates.map((message) => ({
    value: message.id,
    label: message.name,
    type: message.templateType,
    linkType: message.linkType,
  }));

const FormMonetizationOrder = (props) => {
  const editingOrder = props.monetizationOrder || {};

  const sales = useSelector(selectSupplierByType(supplierTypes.SALE));
  const leadFields = useSelector((state) => state.LeadField.leadFields);
  const country = useSelector(actualCountry);
  const spreadsheetLeadFields = leadFields.filter(
    ({ name, typeField }) =>
      name !== 'createdAt' && typeField !== 'async-select',
  );

  const [netRebate, setNetRebate] = useState(editingOrder.netRebate || false);
  const [bypassLogicOnCall, setBypassLogicOnCall] = useState(
    editingOrder.bypassLogicOnCall || false,
  );

  const trackingMessageTemplates = useSelector(
    selectMessageTemplateByMonetizationOrder(editingOrder.id),
  );

  const initialQuery = editingOrder.query || null;
  const initialSqlQuery = editingOrder.sqlQuery || null;
  const initialJsonLogicQuery = editingOrder.jsonLogicQuery || null;

  const [selectedQuery, setSelectedQuery] = useState(initialQuery);
  const [selectedSqlQuery, setSelectedSqlQuery] = useState(initialSqlQuery);
  const [selectedJsonLogicQuery, setSelectedJsonLogicQuery] = useState(
    initialJsonLogicQuery,
  );

  const dispatch = useDispatch();

  const handleSubmit = (values) => {
    const { reasons, supplierId, sendLeadType, googleSpreadsheetId } = values;

    const reasonsArray = !reasons
      ? []
      : Array.isArray(reasons)
        ? reasons
        : reasons.split(',');

    const jsonLogicQuery = selectedJsonLogicQuery?.logic
      ? selectedJsonLogicQuery
      : '';

    const monetizationOrder = {
      ...values,
      bypassLogicOnCall,
      query: selectedQuery || '',
      sqlQuery: selectedSqlQuery || '',
      jsonLogicQuery,
      reasons: reasonsArray,
      ...(supplierId && { netRebate }),
      ...(sendLeadType === 'googleSpreadsheets' &&
        !googleSpreadsheetId && { googleSpreadsheetId: 'new' }),
    };

    dispatch(
      props.create
        ? monetizationOrdersActions.createMonetizationOrder(monetizationOrder)
        : monetizationOrdersActions.updateMonetizationOrder({
            id: editingOrder.id,
            ...monetizationOrder,
          }),
    );
    props.closeModal();
  };

  const handleQueryTree = (jsonTree, sqlQuery, jsonLogicQuery) => {
    setSelectedQuery(jsonTree);
    setSelectedSqlQuery(sqlQuery);
    setSelectedJsonLogicQuery(jsonLogicQuery);
  };

  const { t } = useTranslation();
  // The texts below reach the lead, word for word or through the agent: they
  // are written in the language of the country, not in the CRM's.
  const languageNote = writeInCountryLanguage(t, country?.languageTag);

  return (
    <Card>
      <CardBody>
        <Form
          initialValues={{
            // `aiEnabled` is deliberately absent: that absence is what makes
            // the switch default to OFF. Don't add `aiEnabled: true` by
            // analogy with `aiClosureAllowed` below — its absence means ON
            // to the engine (see formValidation.js), so the two need
            // opposite defaults; adding `aiEnabled: true` here would flip
            // every offer of an AI-enabled country into the mandatory-fields
            // state at once. Also, `defaultChecked` on both Switches below
            // is inert: availity's Input derives `checked` from the Formik
            // value and spreads `defaultChecked` after it, so React treats
            // the checkbox as controlled and ignores it.
            aiPitch: '',
            aiQualificationCriteria: '',
            aiClosingMessage: '',
            aiProposalMessage: '',
            aiClosureAllowed: true,
            ...editingOrder,
            reasons: Array.isArray(editingOrder.reasons)
              ? editingOrder.reasons.join(',')
              : editingOrder.reasons,
            sendSchedule: editingOrder.sendSchedule || defaultSendSchedule(),
          }}
          validationSchema={formValidation}
          onSubmit={handleSubmit}>
          {({ isValid, values }) => (
            <>
              <Row>
                <Col md="3">
                  <Input id="name" name="name" />
                </Col>
                <Col md="3">
                  <Select
                    name="customerId"
                    id="customerId"
                    label="Customer"
                    isMulti={false}
                    options={
                      Array.isArray(props.customers)
                        ? props.customers.map(({ id, name }) => ({
                            label: name,
                            value: id,
                          }))
                        : []
                    }
                  />
                </Col>
                <Col md="2">
                  <Date
                    id="startDate"
                    name="startDate"
                    label="Start Date"
                    format="DD/MM/YYYY"
                  />
                </Col>
                <Col md="2">
                  <Date
                    id="endDate"
                    name="endDate"
                    format="DD/MM/YYYY"
                    label="End Date"
                  />
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <Input
                    id="priority"
                    name="priority"
                    type="number"
                    step="1"
                    subText={
                      country?.aiModule
                        ? t(
                            'Whole number, 1 first. It orders the offers of one acquisition order between themselves, and the AI agent proposes them in this order when a lead qualifies for more than one. At equal numbers the offer with the higher payout goes first.',
                          )
                        : t(
                            'Whole number, 1 first. It is not the priority that sorts the call center queue, which lives on the acquisition order.',
                          )
                    }
                  />
                </Col>
              </Row>
              <Row className="mt-3 mb-2">
                <Col xs="auto" className="d-flex align-items-center">
                  <h5 className="m-0">Logical Condition</h5>
                </Col>
                <Col>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col md="10">
                  <FormGroup>
                    <QueryBuilder
                      label="Logical condition for selecting leads to send"
                      fields={leadFields}
                      onChange={handleQueryTree}
                      initialQuery={initialQuery}
                      subText={t(
                        'Evaluated against the data the lead already has — it never asks her anything. It closes the leads that skip the call center, it filters which orders an operator may pick when closing a call, and it does the same to the AI agent: the agent may still propose an offer whose condition the lead does not satisfy, but it cannot close it. Left empty, it never closes anyone automatically but stays available to every operator and to the agent.',
                      )}
                    />
                  </FormGroup>
                </Col>
                <Col md="2">
                  <Switch
                    name="bypassLogicOnCall"
                    label="Bypass Logic On Call?"
                    defaultChecked={bypassLogicOnCall}
                    onClick={() => setBypassLogicOnCall(!bypassLogicOnCall)}
                  />
                </Col>
              </Row>
              {country?.aiModule && (
                <>
                  <Row className="mt-3 mb-2">
                    <Col xs="auto" className="d-flex align-items-center">
                      <h5 className="m-0">{t('AI instructions')}</h5>
                    </Col>
                    <Col>
                      <hr />
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <Switch
                        name="aiEnabled"
                        label={t('Can an AI agent use this offer?')}
                        defaultChecked={values.aiEnabled === true}
                        subText={t(
                          'Off, this offer does not exist for the agent: it is never proposed and never closed. On, the texts below become mandatory, because an agent with nothing to say about an offer should not be given it.',
                        )}
                      />
                    </Col>
                  </Row>
                  <Collapse isOpen={values.aiEnabled === true}>
                    <Row>
                      <Col md="6">
                        <Input
                          type="textarea"
                          name="aiPitch"
                          label="How to pitch it"
                          required={values.aiEnabled === true}
                          rows="4"
                          subText={`${t(
                            'How the agent should present this product to the lead, in plain language.',
                          )} ${languageNote}`}
                        />
                      </Col>
                      <Col md="6">
                        <Input
                          type="textarea"
                          name="aiQualificationCriteria"
                          label="Who qualifies"
                          required={values.aiEnabled === true}
                          rows="4"
                          subText={`${t(
                            'What the agent must establish by talking to the lead. This is not the logical condition above: that one decides whether a lead can be closed with the data already on file, this one guides what to ask when that data is missing.',
                          )} ${languageNote}`}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <Switch
                          name="aiClosureAllowed"
                          label={t('Can the AI close it alone?')}
                          defaultChecked={values.aiClosureAllowed !== false}
                          subText={t(
                            'Turn it off for products too delicate to close without a person: the agent still collects the interest, and an operator confirms the closure without having to call the lead again.',
                          )}
                        />
                      </Col>
                      <Col md="6">
                        <Collapse isOpen={values.aiClosureAllowed !== false}>
                          <Input
                            type="textarea"
                            name="aiClosingMessage"
                            label="What happens next"
                            required={
                              values.aiEnabled === true &&
                              values.aiClosureAllowed !== false
                            }
                            rows="3"
                            subText={`${t(
                              'The last thing the lead reads once the agent has closed positively. Describe what the customer will do next, for example that they will get in touch to arrange the next step. Sent word for word, so write it as you would to the lead. It is required while the agent can close on its own; turn that switch off and the proposal message takes its place.',
                            )} ${languageNote}`}
                          />
                        </Collapse>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <Collapse isOpen={values.aiClosureAllowed === false}>
                          <Input
                            type="textarea"
                            name="aiProposalMessage"
                            label="What happens next, when a person must confirm"
                            required={
                              values.aiEnabled === true &&
                              values.aiClosureAllowed === false
                            }
                            rows="3"
                            subText={`${t(
                              'The last thing the lead reads when the agent has recorded her interest and handed the conversation to an operator. Nothing is settled yet, so do not promise what the customer will do: say that she will be contacted. Sent word for word. With more than one offer proposed together the sentence comes from the acquisition order instead.',
                            )} ${languageNote}`}
                          />
                        </Collapse>
                      </Col>
                    </Row>
                  </Collapse>
                </>
              )}
              <Row className="mt-3 mb-2">
                <Col xs="auto" className="d-flex align-items-center">
                  <h5 className="m-0">Payout & Goals</h5>
                </Col>
                <Col>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col md="2">
                  <Input
                    id="leads"
                    name="leads"
                    label="Goal Leads"
                    min={0}
                    type="number"
                    step="1"
                  />
                </Col>
                <Col md="2">
                  <Input
                    id="payout"
                    name="payout"
                    addonType="prepend"
                    min={0}
                    type="number"
                    step="0.01">
                    <InputGroupAddon addonType="prepend">€</InputGroupAddon>
                  </Input>
                </Col>
                <Col md="2">
                  <Input
                    id="percentageRebate"
                    name="percentageRebate"
                    addonType="append"
                    label="Rebate"
                    min={0}
                    type="number"
                    step="0.01">
                    <InputGroupAddon addonType="append">%</InputGroupAddon>
                  </Input>
                </Col>
                <Col md="3">
                  <Select
                    name="messageTemplateId"
                    id="messageTemplateId"
                    label="Message on success"
                    isMulti={false}
                    options={[
                      { value: 'none', label: 'None', type: null },
                      ...messageTemplateForSelect(props.messageTemplates),
                    ]}
                    components={{ Option: MessageTemplateSelectOption }}
                  />
                </Col>
                <Col md="2">
                  <Collapse
                    isOpen={
                      values.messageTemplateId &&
                      values.messageTemplateId !== 'none'
                    }>
                    <Input
                      id="messageSendDelay"
                      name="messageSendDelay"
                      label="Send Delay"
                      min={0}
                      type="number"
                      step="1"
                    />
                    <p className="font-size-12 text-muted">
                      Value in days, 0 = now
                    </p>
                  </Collapse>
                </Col>
              </Row>
              <Row className="mt-3 mb-2">
                <Col xs="auto" className="d-flex align-items-center">
                  <h5 className="m-0">Lead Delivery</h5>
                </Col>
                <Col>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <Select
                    name="sendLeadType"
                    id="sendLeadType"
                    label="How Send Leads?"
                    options={sendLeadTypes}
                  />
                </Col>
                <Col md="6">
                  <Collapse
                    isOpen={
                      values.sendLeadType === 'dataEntry' ||
                      values.sendLeadType === 'trackingPixel'
                    }>
                    <Input
                      id="landingPageCustomer"
                      name="landingPageCustomer"
                      label="Landing Page Customer"
                      type="textarea"
                    />
                  </Collapse>
                  <Collapse
                    isOpen={values.sendLeadType === 'googleSpreadsheets'}>
                    <Select
                      name="spreadsheetLeadFieldIds"
                      id="spreadsheetLeadFieldIds"
                      label="Fields to export"
                      isMulti
                      isDisabled={!!editingOrder?.googleSpreadsheetId}
                      isSearchable
                      options={spreadsheetLeadFields.map(({ id, name }) => ({
                        label: t(name),
                        value: id,
                      }))}
                    />
                    <p className="font-size-12 text-muted">
                      Select the fields that you want to export to the
                      spreadsheet. Once the spreadsheet is created you can't
                      change the fields. The field <b>"createdAt"</b> is always
                      exported as last column and can't be selected here.
                    </p>
                  </Collapse>
                </Col>
                <Col md="3">
                  <Collapse isOpen={values.sendLeadType === 'trackingPixel'}>
                    <Select
                      name="trackingMessageTemplateId"
                      id="trackingMessageTemplateId"
                      label="Message with Tracking Link"
                      isMulti={false}
                      options={messageTemplateForSelect(
                        trackingMessageTemplates,
                      )}
                      components={{ Option: MessageTemplateSelectOption }}
                    />
                  </Collapse>
                </Col>
              </Row>
              <Collapse isOpen={values.sendLeadType === 'webService'}>
                <Row>
                  <Col md="3">
                    <Select
                      name="webService"
                      id="webService"
                      label="Web Service"
                      options={customerWebServices}
                    />
                  </Col>
                </Row>
              </Collapse>
              <Collapse
                isOpen={
                  values.sendLeadType === 'webService' ||
                  values.sendLeadType === 'googleSpreadsheets'
                }>
                <Row className="mt-2">
                  <Col md="12">
                    <Switch
                      name="sendSchedule.enabled"
                      label={t('Activate Sending Schedule')}
                    />
                  </Col>
                </Row>
                <Collapse isOpen={!!values.sendSchedule?.enabled}>
                  <Row className="mb-3">
                    <Col md="12">
                      <SendScheduleMatrix />
                    </Col>
                  </Row>
                </Collapse>
              </Collapse>
              <Row className="mt-3 mb-2">
                <Col xs="auto" className="d-flex align-items-center">
                  <h5 className="m-0">Management</h5>
                </Col>
                <Col>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <Input
                    id="costPositiveClosure"
                    name="costPositiveClosure"
                    label="Cost per positive closure"
                    addonType="prepend"
                    min={0}
                    type="number"
                    step="0.01">
                    <InputGroupAddon addonType="prepend">€</InputGroupAddon>
                  </Input>
                </Col>
                <Col md="3">
                  <Input
                    id="validLeads"
                    name="validLeads"
                    label="Total Valid Leads"
                    min={0}
                    type="number"
                    step="1"
                  />
                </Col>
                <Col md="6">
                  <Input id="reasons" name="reasons" type="textarea" />
                  <p className="font-size-12 text-muted">
                    Insert comma separated strings
                  </p>
                </Col>
              </Row>
              <Row className="mt-3 mb-2">
                <Col xs="auto" className="d-flex align-items-center">
                  <h5 className="m-0">Sales Commission</h5>
                </Col>
                <Col>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col md="3">
                  <Select
                    name="supplierId"
                    id="supplierId"
                    label="Sale"
                    options={
                      Array.isArray(sales)
                        ? sales.map(({ id, name }) => ({
                            value: id,
                            label: name,
                          }))
                        : []
                    }
                  />
                </Col>
                <Col md="3">
                  <Collapse isOpen={!!values.supplierId}>
                    <Input
                      id="percentageRevenueShare"
                      name="percentageRevenueShare"
                      label="Percentage Revenue Share"
                      addonType="append"
                      min={0}
                      type="number"
                      step="0.1">
                      <InputGroupAddon addonType="append">%</InputGroupAddon>
                    </Input>
                  </Collapse>
                </Col>
                <Col md="3">
                  <Collapse isOpen={!!values.supplierId}>
                    <Input
                      id="percentageMarginShare"
                      name="percentageMarginShare"
                      label="Percentage Margin Share"
                      min={0}
                      type="number"
                      step="0.1">
                      <InputGroupAddon addonType="append">%</InputGroupAddon>
                    </Input>
                  </Collapse>
                </Col>
                <Col md="3">
                  <Collapse isOpen={!!values.supplierId}>
                    <Switch
                      name="netRebate"
                      label="Active?"
                      defaultChecked={netRebate}
                      onClick={() => setNetRebate(!netRebate)}
                    />
                  </Collapse>
                </Col>
              </Row>
              <div className="modal-footer">
                <SubmitButton
                  disabled={!isValid || props.loading}
                  create={props.create}
                />
              </div>
            </>
          )}
        </Form>
      </CardBody>
    </Card>
  );
};

export default FormMonetizationOrder;
